import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { AuthContext } from "../auth/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { COLORS as C, RADIUS } from "../constants/ui";
import { getMyProjects } from "../api/projectApi";
import {
  createExpenseClaim,
  deleteExpenseClaim,
  fetchExpenseReceipt,
  getExpenseClaim,
  getExpenseClaims,
  getExpenseCounts,
  getExpenseSettings,
  hodApproveExpense,
  hodReconcileExpense,
  hodRejectExpense,
  saApproveExpense,
  saRejectExpense,
  submitExpenseClaim,
  updateExpenseClaim,
  updateExpenseSettings,
  uploadExpenseReceipt,
} from "../api/expenseApi";
import { formatInr } from "../utils/formatInr";
import { isSuperAdminTier } from "../constants/orgTiers";
import {
  EXPENSE_CLAIM_STATUS,
  EXPENSE_CLAIM_TYPE,
  STATUS_COLORS,
  STATUS_LABELS,
  TYPE_LABELS,
} from "../constants/expenseClaims";
import { BUDGET_ORGANIZATIONS } from "../constants/organizations";
import { DEPARTMENTS } from "../constants/departments";

const currentYear = () => new Date().getFullYear();

const btnPrimary = {
  background: C.accent,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: "0.79rem",
};

const btnGhost = {
  background: "#fff",
  color: C.ink,
  border: `1px solid ${C.upcoming}`,
  borderRadius: 8,
  padding: "8px 14px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "0.85rem",
};

const btnDanger = {
  ...btnGhost,
  color: C.danger,
  borderColor: C.dangerSoft,
};

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || { bg: C.sunken, color: C.inkSoft };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: RADIUS.pill,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: style.bg,
        color: style.color,
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: C.inkSoft, marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  borderRadius: 8,
  border: `1px solid ${C.upcoming}`,
  fontSize: "0.9rem",
  boxSizing: "border-box",
};

function emptyForm(userDept) {
  return {
    claimType: EXPENSE_CLAIM_TYPE.POST,
    title: "",
    description: "",
    amountInr: "",
    projectId: "",
    organization: "",
    eventDate: "",
    department: userDept || "",
  };
}

export default function ExpenseClaims() {
  const { user, role } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const currentRole = (role || "").toLowerCase();
  const isSuper = isSuperAdminTier(currentRole);
  const perms = user?.permissions || [];
  const canSubmit = perms.includes("expense.submit");
  const canApprove = perms.includes("expense.approve");

  const defaultTab = useMemo(() => {
    if (isSuper) return "escalated";
    if (canApprove && !canSubmit) return "hod_queue";
    return "mine";
  }, [isSuper, canApprove, canSubmit]);

  // Deep link from a notification: ?tab=hod_queue&claimId=42
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const urlClaimId = searchParams.get("claimId");

  const [tab, setTab] = useState(urlTab || defaultTab);
  const [selectedId, setSelectedId] = useState(urlClaimId ? Number(urlClaimId) : null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(() => emptyForm(user?.department));
  const [editMode, setEditMode] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [actionRemark, setActionRemark] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [reconcileAmount, setReconcileAmount] = useState("");
  const [settingsForm, setSettingsForm] = useState({ defaultThresholdInr: 50000, departmentThresholds: [] });
  const [busy, setBusy] = useState(false);

  useBodyScrollLock(showCreate);

  // defaultTab settles once permissions load, so it would otherwise clobber a
  // tab that came from the URL. Honour the link the first time, then behave
  // exactly as before.
  const urlTabConsumed = useRef(Boolean(urlTab));
  useEffect(() => {
    if (urlTabConsumed.current) {
      urlTabConsumed.current = false;
      return;
    }
    setTab(defaultTab);
  }, [defaultTab]);

  // Open the claim the notification pointed at, then drop the params so a
  // refresh or a later tab change does not keep reopening it.
  useEffect(() => {
    if (!urlTab && !urlClaimId) return;
    if (urlClaimId) setSelectedId(Number(urlClaimId));
    if (urlTab) setTab(urlTab);
    setSearchParams({}, { replace: true });
  }, [urlTab, urlClaimId, setSearchParams]);

  const listScope =
    tab === "hod_queue"
      ? "hod_queue"
      : tab === "dept_history"
        ? "dept_history"
        : tab === "escalated"
          ? "escalated"
          : "mine";

  const { data: claims = [], isLoading } = useQuery({
    queryKey: ["expense-claims", listScope],
    queryFn: async () => {
      const res = await getExpenseClaims({ scope: listScope });
      return res.data;
    },
    enabled: tab !== "settings",
  });

  const { data: counts } = useQuery({
    queryKey: ["expense-counts"],
    queryFn: async () => {
      const res = await getExpenseCounts();
      return res.data;
    },
  });

  const { data: detail } = useQuery({
    queryKey: ["expense-claim", selectedId],
    queryFn: async () => {
      const res = await getExpenseClaim(selectedId);
      return res.data;
    },
    enabled: Boolean(selectedId),
  });

  const { data: myProjects = [] } = useQuery({
    queryKey: ["my-projects-expense"],
    queryFn: async () => {
      const res = await getMyProjects();
      return res.data || [];
    },
    enabled: canSubmit,
  });

  const { data: settings, refetch: refetchSettings } = useQuery({
    queryKey: ["expense-settings"],
    queryFn: async () => {
      const res = await getExpenseSettings();
      return res.data;
    },
    enabled: isSuper && tab === "settings",
  });

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        defaultThresholdInr: settings.defaultThresholdInr ?? 50000,
        departmentThresholds: settings.departmentThresholds || [],
      });
    }
  }, [settings]);

  const claim = detail?.claim;
  const hodThreshold = detail?.hodThresholdInr ?? 50000;

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["expense-claims"] });
    qc.invalidateQueries({ queryKey: ["expense-counts"] });
    if (selectedId) qc.invalidateQueries({ queryKey: ["expense-claim", selectedId] });
  }, [qc, selectedId]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.amountInr && form.amountInr !== 0) return toast.error("Amount is required");
    setBusy(true);
    try {
      const body = {
        claimType: form.claimType,
        title: form.title.trim(),
        description: form.description.trim() || null,
        amountInr: Number(form.amountInr),
        department: form.department || user?.department,
        budgetYear: currentYear(),
        projectId: form.projectId || null,
        organization: form.organization || null,
        eventDate: form.claimType === EXPENSE_CLAIM_TYPE.POST ? form.eventDate || null : null,
      };
      const res = await createExpenseClaim(body);
      if (receiptFile && form.claimType === EXPENSE_CLAIM_TYPE.POST) {
        await uploadExpenseReceipt(res.data.id, receiptFile);
      }
      toast.success("Claim created");
      setShowCreate(false);
      setForm(emptyForm(user?.department));
      setReceiptFile(null);
      setSelectedId(res.data.id);
      setTab("mine");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create claim");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!claim) return;
    setBusy(true);
    try {
      await updateExpenseClaim(claim.id, {
        title: form.title,
        description: form.description,
        amountInr: Number(form.amountInr),
        projectId: form.projectId || null,
        organization: form.organization || null,
        eventDate: form.eventDate || null,
      });
      toast.success("Claim updated");
      setEditMode(false);
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUploadReceipt = async (forProof = false) => {
    if (!claim || !receiptFile) return toast.error("Choose a file first");
    setBusy(true);
    try {
      await uploadExpenseReceipt(claim.id, receiptFile);
      toast.success(forProof ? "Proof uploaded — sent for reconciliation" : "Receipt uploaded");
      setReceiptFile(null);
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!claim) return;
    setBusy(true);
    try {
      await submitExpenseClaim(claim.id);
      toast.success("Submitted to HOD");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!claim || !window.confirm("Delete this draft claim?")) return;
    setBusy(true);
    try {
      await deleteExpenseClaim(claim.id);
      toast.success("Deleted");
      setSelectedId(null);
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const handleHodApprove = async () => {
    if (!claim) return;
    const over = claim.amountInr > hodThreshold;
    if (over && !actionRemark.trim()) {
      return toast.error(`Amount exceeds HOD threshold (${formatInr(hodThreshold)}). Remark required to escalate.`);
    }
    setBusy(true);
    try {
      await hodApproveExpense(claim.id, { remark: actionRemark.trim() || undefined });
      if (over) {
        toast.success("Escalated to Superadmin");
      } else if (claim.claimType === EXPENSE_CLAIM_TYPE.PRE_APPROVED) {
        toast.success("Approved — claimant must upload proof before budget is deducted");
        setTab("dept_history");
      } else {
        toast.success("Approved — spend added to project budget");
        setTab("dept_history");
      }
      setActionRemark("");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approve failed");
      invalidate();
    } finally {
      setBusy(false);
    }
  };

  const handleHodReject = async () => {
    if (!claim || !rejectReason.trim()) return toast.error("Rejection reason is required");
    setBusy(true);
    try {
      await hodRejectExpense(claim.id, { rejectionReason: rejectReason.trim() });
      toast.success("Rejected");
      setRejectReason("");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const handleReconcile = async () => {
    if (!claim) return;
    setBusy(true);
    try {
      await hodReconcileExpense(claim.id, { adjustedAmountInr: Number(reconcileAmount) });
      toast.success("Reconciled and approved");
      setReconcileAmount("");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Reconcile failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSaApprove = async () => {
    if (!claim) return;
    setBusy(true);
    try {
      await saApproveExpense(claim.id, { remark: actionRemark.trim() || undefined });
      toast.success("Approved");
      setActionRemark("");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSaReject = async () => {
    if (!claim || !rejectReason.trim()) return toast.error("Rejection reason is required");
    setBusy(true);
    try {
      await saRejectExpense(claim.id, { rejectionReason: rejectReason.trim() });
      toast.success("Rejected");
      setRejectReason("");
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSettings = async () => {
    setBusy(true);
    try {
      await updateExpenseSettings(settingsForm);
      toast.success("Settings saved");
      refetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const openClaim = (c) => {
    setSelectedId(c.id);
    setEditMode(false);
    setActionRemark("");
    setRejectReason("");
    setReconcileAmount(c.adjustedAmountInr != null ? String(c.adjustedAmountInr) : String(c.amountInr || ""));
    setForm({
      claimType: c.claimType,
      title: c.title || "",
      description: c.description || "",
      amountInr: String(c.amountInr ?? ""),
      projectId: c.projectId || "",
      organization: c.organization || "",
      eventDate: c.eventDate ? String(c.eventDate).slice(0, 10) : "",
      department: c.department || "",
    });
  };

  const viewReceipt = async (id) => {
    try {
      const res = await fetchExpenseReceipt(id);
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open receipt");
    }
  };

  const tabs = [
    canSubmit ? { key: "mine", label: "My Claims", badge: counts?.myDrafts } : null,
    canApprove ? { key: "hod_queue", label: "HOD Queue", badge: counts?.pendingHod } : null,
    canApprove ? { key: "dept_history", label: "Dept history" } : null,
    isSuper ? { key: "escalated", label: "SA Escalations", badge: counts?.escalatedSa } : null,
    isSuper ? { key: "settings", label: "Threshold Settings" } : null,
  ].filter(Boolean);

  const showClaimantColumn = tab !== "mine";
  const showProjectColumn = tab === "dept_history" || tab === "hod_queue";

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.65rem", fontWeight: 800, color: C.ink }}>Expense Claims</h1>
          <p style={{ margin: "6px 0 0", color: C.inkSoft, fontSize: "0.9rem" }}>
            Post-event and pre-approved claims with HOD approval and superadmin escalation above threshold.
          </p>
        </div>
        {canSubmit ? (
          <button type="button" style={btnPrimary} onClick={() => { setShowCreate(true); setForm(emptyForm(user?.department)); setReceiptFile(null); }}>
            + New claim
          </button>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setTab(t.key); setSelectedId(null); }}
            style={{
              ...btnGhost,
              background: tab === t.key ? C.accentSoft : "#fff",
              borderColor: tab === t.key ? "#7CCAF0" : C.upcoming,
              color: tab === t.key ? C.accentInk : C.ink,
            }}
          >
            {t.label}
            {t.badge > 0 ? (
              <span style={{ marginLeft: 6, background: C.warn, color: "#fff", borderRadius: RADIUS.pill, padding: "1px 7px", fontSize: "0.7rem" }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "dept_history" ? (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontSize: "0.85rem", color: "#0369a1" }}>
          <strong>Where approved expenses appear:</strong> Post-event claims show under <strong>Budget → click project → spend lines</strong> once fully approved.
          Pre-approved claims stay here as <em>Awaiting proof</em> until the employee uploads a receipt, then return to HOD Queue for reconciliation.
        </div>
      ) : null}

      {tab === "settings" ? (
        <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: RADIUS.card, padding: 20 }}>
          <h3 style={{ marginTop: 0 }}>HOD approval threshold</h3>
          <p style={{ color: C.inkSoft, fontSize: "0.85rem" }}>
            Claims above this amount require HOD remark and escalate to Superadmin for final approval.
          </p>
          <Field label="Default threshold (INR)">
            <input
              type="number"
              min="0"
              style={inputStyle}
              value={settingsForm.defaultThresholdInr}
              onChange={(e) => setSettingsForm((s) => ({ ...s, defaultThresholdInr: Number(e.target.value) }))}
            />
          </Field>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Per-department overrides</div>
            {DEPARTMENTS.map((dept) => {
              const row = settingsForm.departmentThresholds.find((d) => d.department === dept);
              const val = row?.maxAmountInr ?? "";
              return (
                <div key={dept} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  <span style={{ width: 160, fontSize: "0.85rem" }}>{dept}</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="Use default"
                    style={{ ...inputStyle, maxWidth: 200 }}
                    value={val}
                    onChange={(e) => {
                      const n = e.target.value === "" ? null : Number(e.target.value);
                      setSettingsForm((s) => {
                        const rest = s.departmentThresholds.filter((d) => d.department !== dept);
                        if (n == null || Number.isNaN(n)) return { ...s, departmentThresholds: rest };
                        return { ...s, departmentThresholds: [...rest, { department: dept, maxAmountInr: n }] };
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
          <button type="button" style={{ ...btnPrimary, marginTop: 12 }} disabled={busy} onClick={handleSaveSettings}>
            Save settings
          </button>
        </div>
      ) : (
        // The 380px detail pane plus the list needs ~700px; on a phone the two
        // stack instead. The list keeps `overflow: hidden`, so the table gets
        // its own horizontal scroller below.
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : selectedId ? "1fr 380px" : "1fr",
            gap: 16,
          }}
        >
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: RADIUS.card, overflow: "hidden", minWidth: 0 }}>
            {isLoading ? (
              <div style={{ padding: 24, color: C.inkSoft }}>Loading…</div>
            ) : claims.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: C.inkSoft }}>No claims in this queue.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
              {/* Floor only kicks in on narrow screens — at 520px the table
                  still fits beside the detail pane on a laptop, so desktop
                  never gains a scrollbar it didn't have. */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: 520 }}>
                <thead>
                  <tr style={{ background: C.sunken, textAlign: "left" }}>
                    <th style={{ padding: "10px 14px" }}>Title</th>
                    <th style={{ padding: "10px 14px" }}>Type</th>
                    <th style={{ padding: "10px 14px" }}>Amount</th>
                    <th style={{ padding: "10px 14px" }}>Status</th>
                    {showProjectColumn ? <th style={{ padding: "10px 14px" }}>Project</th> : null}
                    {showClaimantColumn ? <th style={{ padding: "10px 14px" }}>Claimant</th> : null}
                    <th style={{ padding: "10px 14px" }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => openClaim(c)}
                      style={{
                        cursor: "pointer",
                        background: selectedId === c.id ? C.accentSoft : "transparent",
                        borderTop: `1px solid ${C.line}`,
                      }}
                    >
                      <td style={{ padding: "11px 14px", fontWeight: 600 }}>{c.title}</td>
                      <td style={{ padding: "11px 14px" }}>{TYPE_LABELS[c.claimType] || c.claimType}</td>
                      <td style={{ padding: "11px 14px" }}>{formatInr(c.adjustedAmountInr ?? c.amountInr)}</td>
                      <td style={{ padding: "11px 14px" }}><StatusBadge status={c.status} /></td>
                      {showProjectColumn ? <td style={{ padding: "11px 14px" }}>{c.projectName || c.projectId || "—"}</td> : null}
                      {showClaimantColumn ? <td style={{ padding: "11px 14px" }}>{c.claimantName || "—"}</td> : null}
                      <td style={{ padding: "11px 14px", color: C.inkSoft }}>
                        {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {selectedId && claim ? (
            <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: RADIUS.card, padding: 18, alignSelf: "start", position: "sticky", top: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: "1rem" }}>Claim #{claim.id}</h3>
                <StatusBadge status={claim.status} />
              </div>

              {editMode ? (
                <>
                  <Field label="Title">
                    <input style={inputStyle} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                  </Field>
                  <Field label="Amount (INR)">
                    <input type="number" style={inputStyle} value={form.amountInr} onChange={(e) => setForm((f) => ({ ...f, amountInr: e.target.value }))} />
                  </Field>
                  <Field label="Description">
                    <textarea style={{ ...inputStyle, minHeight: 72 }} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                  </Field>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" style={btnPrimary} disabled={busy} onClick={handleSaveEdit}>Save</button>
                    <button type="button" style={btnGhost} onClick={() => setEditMode(false)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: "0.85rem", color: C.ink, lineHeight: 1.6 }}>
                    <div><strong>{claim.title}</strong></div>
                    <div>{TYPE_LABELS[claim.claimType]} · {formatInr(claim.amountInr)}</div>
                    {claim.projectName ? <div>Project: {claim.projectName}</div> : null}
                    {claim.department ? <div>Dept: {claim.department}</div> : null}
                    {claim.eventDate ? <div>Event: {String(claim.eventDate).slice(0, 10)}</div> : null}
                    {claim.description ? <div style={{ marginTop: 8, color: C.inkSoft }}>{claim.description}</div> : null}
                    {claim.status === EXPENSE_CLAIM_STATUS.PROOF_PENDING ? (
                      <div style={{ marginTop: 10, padding: "8px 10px", background: "#e0e7ff", borderRadius: 8, fontSize: "0.8rem", color: "#3730a3" }}>
                        Waiting for {claim.claimantName || "claimant"} to upload proof. Budget is not deducted yet.
                      </div>
                    ) : null}
                    {claim.status === EXPENSE_CLAIM_STATUS.FINAL_APPROVED && claim.projectName ? (
                      <div style={{ marginTop: 10, padding: "8px 10px", background: C.doneSoft, borderRadius: 8, fontSize: "0.8rem", color: "#166534" }}>
                        {claim.budgetDeducted
                          ? `Spend recorded on project "${claim.projectName}" — open Budget page and click the project to see the line item.`
                          : "Approved (no project linked — spend not added to project budget)."}
                      </div>
                    ) : null}
                    {claim.hodRemark ? <div style={{ marginTop: 8 }}>HOD remark: {claim.hodRemark}</div> : null}
                    {claim.rejectionReason ? <div style={{ marginTop: 8, color: "#b91c1c" }}>Rejected: {claim.rejectionReason}</div> : null}
                    {tab !== "mine" && claim.claimantName ? <div style={{ marginTop: 8 }}>Claimant: {claim.claimantName}</div> : null}
                    <div style={{ marginTop: 8, fontSize: "0.78rem", color: C.inkFaint }}>
                      HOD threshold: {formatInr(hodThreshold)}
                    </div>
                  </div>

                  {claim.hasReceipt ? (
                    <button type="button" style={{ ...btnGhost, marginTop: 12, width: "100%" }} onClick={() => viewReceipt(claim.id)}>
                      View receipt
                    </button>
                  ) : null}

                  {canSubmit && claim.claimantId === user?.id && claim.status === EXPENSE_CLAIM_STATUS.DRAFT ? (
                    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                      <button type="button" style={btnGhost} onClick={() => setEditMode(true)}>Edit</button>
                      {claim.claimType === EXPENSE_CLAIM_TYPE.POST ? (
                        <>
                          <input type="file" accept=".pdf,image/*" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                          <button type="button" style={btnGhost} disabled={!receiptFile || busy} onClick={() => handleUploadReceipt(false)}>Upload receipt</button>
                        </>
                      ) : null}
                      <button type="button" style={btnPrimary} disabled={busy} onClick={handleSubmit}>Submit to HOD</button>
                      <button type="button" style={btnDanger} disabled={busy} onClick={handleDelete}>Delete draft</button>
                    </div>
                  ) : null}

                  {canSubmit && claim.claimantId === user?.id && claim.status === EXPENSE_CLAIM_STATUS.PROOF_PENDING ? (
                    <div style={{ marginTop: 14 }}>
                      <p style={{ fontSize: "0.82rem", color: C.inkSoft }}>Upload proof of spend to continue.</p>
                      <input type="file" accept=".pdf,image/*" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                      <button type="button" style={{ ...btnPrimary, marginTop: 8, width: "100%" }} disabled={!receiptFile || busy} onClick={() => handleUploadReceipt(true)}>
                        Upload proof
                      </button>
                    </div>
                  ) : null}

                  {canApprove && claim.status === EXPENSE_CLAIM_STATUS.PENDING_HOD ? (
                    <div style={{ marginTop: 14 }}>
                      {claim.amountInr > hodThreshold ? (
                        <p style={{ fontSize: "0.8rem", color: "#c2410c" }}>
                          Over threshold — remark required; will escalate to Superadmin.
                        </p>
                      ) : null}
                      <Field label="Remark (optional unless over threshold)">
                        <textarea style={{ ...inputStyle, minHeight: 60 }} value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} />
                      </Field>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" style={btnPrimary} disabled={busy} onClick={handleHodApprove}>Approve</button>
                        <button type="button" style={btnDanger} disabled={busy} onClick={handleHodReject}>Reject</button>
                      </div>
                      <Field label="Rejection reason">
                        <input style={inputStyle} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      </Field>
                    </div>
                  ) : null}

                  {canApprove && claim.status === EXPENSE_CLAIM_STATUS.PENDING_RECONCILIATION ? (
                    <div style={{ marginTop: 14 }}>
                      <Field label="Adjusted amount (INR)">
                        <input type="number" style={inputStyle} value={reconcileAmount} onChange={(e) => setReconcileAmount(e.target.value)} />
                      </Field>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" style={btnPrimary} disabled={busy} onClick={handleReconcile}>Reconcile & approve</button>
                        <button type="button" style={btnDanger} disabled={busy} onClick={handleHodReject}>Reject</button>
                      </div>
                      <Field label="Rejection reason">
                        <input style={inputStyle} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      </Field>
                    </div>
                  ) : null}

                  {isSuper && claim.status === EXPENSE_CLAIM_STATUS.ESCALATED_SA ? (
                    <div style={{ marginTop: 14 }}>
                      {claim.hodRemark ? <p style={{ fontSize: "0.82rem" }}>HOD: {claim.hodRemark}</p> : null}
                      <Field label="SA remark (optional)">
                        <textarea style={{ ...inputStyle, minHeight: 60 }} value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} />
                      </Field>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" style={btnPrimary} disabled={busy} onClick={handleSaApprove}>Approve</button>
                        <button type="button" style={btnDanger} disabled={busy} onClick={handleSaReject}>Reject</button>
                      </div>
                      <Field label="Rejection reason">
                        <input style={inputStyle} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      </Field>
                    </div>
                  ) : null}

                  {detail?.audit?.length ? (
                    <div style={{ marginTop: 16, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.78rem", color: C.inkSoft, marginBottom: 6 }}>Audit</div>
                      {detail.audit.slice(0, 5).map((a) => (
                        <div key={a.id} style={{ fontSize: "0.75rem", color: C.inkSoft, marginBottom: 4 }}>
                          {a.action} · {a.actorName || "—"} · {a.createdAt ? new Date(a.createdAt).toLocaleString("en-IN") : ""}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>
      )}

      {showCreate ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setShowCreate(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: RADIUS.card, padding: 22, width: "100%", maxWidth: 480, maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>New expense claim</h3>
            <Field label="Type">
              <select style={inputStyle} value={form.claimType} onChange={(e) => setForm((f) => ({ ...f, claimType: e.target.value }))}>
                <option value={EXPENSE_CLAIM_TYPE.POST}>Post-event (receipt required)</option>
                <option value={EXPENSE_CLAIM_TYPE.PRE_APPROVED}>Pre-approved (proof after event)</option>
              </select>
            </Field>
            <Field label="Title">
              <input style={inputStyle} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </Field>
            <Field label="Amount (INR)">
              <input type="number" min="0" style={inputStyle} value={form.amountInr} onChange={(e) => setForm((f) => ({ ...f, amountInr: e.target.value }))} />
            </Field>
            {form.claimType === EXPENSE_CLAIM_TYPE.POST ? (
              <Field label="Event date">
                <input type="date" style={inputStyle} value={form.eventDate} onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))} />
              </Field>
            ) : null}
            <Field label="Project (optional)">
              <select style={inputStyle} value={form.projectId} onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}>
                <option value="">— None —</option>
                {myProjects.map((p) => (
                  <option key={p.projectid || p.id} value={p.projectid || p.id}>
                    {p.projectname || p.name || p.projectid}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Organization (optional)">
              <select style={inputStyle} value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}>
                <option value="">— None —</option>
                {BUDGET_ORGANIZATIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field label="Description">
              <textarea style={{ ...inputStyle, minHeight: 72 }} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </Field>
            {form.claimType === EXPENSE_CLAIM_TYPE.POST ? (
              <Field label="Receipt (PDF or image)">
                <input type="file" accept=".pdf,image/*" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
              </Field>
            ) : null}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" style={btnPrimary} disabled={busy} onClick={handleCreate}>Create draft</button>
              <button type="button" style={btnGhost} onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
